import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as QRCode from 'qrcode';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { authAuditService } from '../../services/authAuditService';

interface TotpFactor {
  id: string;
  friendly_name?: string;
  status?: string;
}

interface EnrollmentState {
  factorId: string;
  uri: string;
  secret: string;
}

interface AdminMfaGateProps {
  children: React.ReactNode;
}

const AdminMfaGate: React.FC<AdminMfaGateProps> = ({ children }) => {
  const { loading, refreshSession, user, isAdmin = false, isAdminLoading = false } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [gateState, setGateState] = useState<
    'allowed' | 'challenge' | 'checking' | 'error' | 'pending-existing' | 'setup'
  >('checking');
  const [challengeCode, setChallengeCode] = useState('');
  const [enrollment, setEnrollment] = useState<EnrollmentState | null>(null);
  const [pendingFactorId, setPendingFactorId] = useState<string | null>(null);
  const [pendingFactorName, setPendingFactorName] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifiedFactorId, setVerifiedFactorId] = useState<string | null>(null);

  const refreshGate = useCallback(async () => {
    if (!user) {
      setGateState('allowed');
      return;
    }

    if (isAdminLoading) {
      setGateState('checking');
      return;
    }

    if (!isAdmin) {
      setGateState('allowed');
      return;
    }

    setGateState('checking');
    setError(null);

    try {
      const [{ data: aalData, error: aalError }, { data: factorData, error: factorError }] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);

      if (aalError) {
        throw aalError;
      }

      if (factorError) {
        throw factorError;
      }

      const factors = (factorData?.all || []) as TotpFactor[];
      const verifiedFactor = factors.find((factor) => factor.status === 'verified');
      const unverifiedFactor = factors.find((factor) => factor.status !== 'verified');

      setPendingFactorId(null);
      setPendingFactorName(null);

      if (aalData?.currentLevel === 'aal2') {
        setVerifiedFactorId(verifiedFactor?.id ?? null);
        setGateState('allowed');
        return;
      }

      if (verifiedFactor) {
        setVerifiedFactorId(verifiedFactor.id);
        setGateState('challenge');
        return;
      }

      if (unverifiedFactor) {
        setPendingFactorId(unverifiedFactor.id);
        setPendingFactorName(unverifiedFactor.friendly_name ?? 'LooKal Admin');
        setVerifiedFactorId(null);
        setGateState('pending-existing');
        return;
      }

      setVerifiedFactorId(null);
      setGateState('setup');
    } catch (mfaError) {
      const message =
        mfaError instanceof Error
          ? mfaError.message
          : typeof mfaError === 'object' && mfaError !== null && 'message' in mfaError
            ? String((mfaError as { message: unknown }).message)
            : 'Failed to verify MFA requirements.';
      setError(message);
      setGateState('error');
    }
  }, [isAdmin, isAdminLoading, user]);

  useEffect(() => {
    if (!loading) {
      void refreshGate();
    }
  }, [loading, refreshGate]);

  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!enrollment?.uri) {
      return;
    }
    const canvas = qrCanvasRef.current;
    if (!canvas) {
      return;
    }
    QRCode.toCanvas(canvas, enrollment.uri, {
      width: 224,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    }).catch((drawError) => {
      const message =
        drawError instanceof Error ? drawError.message : 'Failed to render QR code.';
      setError(message);
    });
  }, [enrollment]);

  const handleEnroll = async () => {
    if (!user) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'LooKal Admin',
        issuer: 'LooKal',
      });

      if (enrollError || !data) {
        throw enrollError || new Error('Failed to start MFA enrollment.');
      }

      setEnrollment({
        factorId: data.id,
        uri: data.totp.uri,
        secret: data.totp.secret,
      });

      await authAuditService.logEvent({
        eventType: 'mfa_enrollment_started',
        email: user.email ?? undefined,
        userId: user.id,
        details: { factor_id: data.id, factor_type: 'totp' },
      });
    } catch (enrollError) {
      const message =
        enrollError instanceof Error
          ? enrollError.message
          : typeof enrollError === 'object' && enrollError !== null && 'message' in enrollError
            ? String((enrollError as { message: unknown }).message)
            : 'Failed to start MFA enrollment.';

      const isDuplicate =
        typeof message === 'string' && /already exists|duplicate|422/i.test(message);

      if (isDuplicate) {
        setEnrollment(null);
        await refreshGate();
        return;
      }

      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnenrollPending = async () => {
    if (!user || !pendingFactorId) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({
        factorId: pendingFactorId,
      });

      if (unenrollError) {
        throw unenrollError;
      }

      await authAuditService.logEvent({
        eventType: 'mfa_enrollment_reset',
        email: user.email ?? undefined,
        userId: user.id,
        details: { factor_id: pendingFactorId, factor_type: 'totp' },
      });

      setPendingFactorId(null);
      setPendingFactorName(null);
      await refreshGate();
    } catch (unenrollError) {
      const message =
        unenrollError instanceof Error
          ? unenrollError.message
          : typeof unenrollError === 'object' && unenrollError !== null && 'message' in unenrollError
            ? String((unenrollError as { message: unknown }).message)
            : 'Failed to reset MFA factor.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinuePending = () => {
    if (!pendingFactorId) {
      return;
    }

    setVerifiedFactorId(pendingFactorId);
    setError(null);
    setChallengeCode('');
    setGateState('challenge');
  };

  const handleVerifyCode = async () => {
    if (!user) {
      return;
    }

    const factorId = enrollment?.factorId ?? verifiedFactorId ?? pendingFactorId;

    if (!factorId || !challengeCode.trim()) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: challengeCode.trim(),
      });

      if (verifyError) {
        throw verifyError;
      }

      await refreshSession();

      await authAuditService.logEvent({
        eventType: enrollment ? 'mfa_enrolled' : 'mfa_verified',
        email: user.email ?? undefined,
        userId: user.id,
        details: { factor_id: factorId, factor_type: 'totp' },
      });

      setChallengeCode('');
      setEnrollment(null);
      await refreshGate();
    } catch (verifyError) {
      const message =
        verifyError instanceof Error
          ? verifyError.message
          : typeof verifyError === 'object' && verifyError !== null && 'message' in verifyError
            ? String((verifyError as { message: unknown }).message)
            : 'Invalid MFA code.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || isAdminLoading || gateState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-sm text-gray-600">Checking admin security requirements...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin || gateState === 'allowed') {
    return <>{children}</>;
  }

  if (gateState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">Admin MFA check failed</h1>
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error || 'We could not verify the admin MFA requirement.'}
          </p>
          <button
            className="mt-6 w-full rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-900"
            onClick={() => void refreshGate()}
            type="button"
          >
            Retry security check
          </button>
        </div>
      </div>
    );
  }

  if (gateState === 'pending-existing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-gray-900">Finish setting up your authenticator</h1>
          <p className="mt-3 text-sm text-gray-600">
            An authenticator factor
            {pendingFactorName ? (
              <>
                {' '}named <span className="font-semibold text-gray-800">&ldquo;{pendingFactorName}&rdquo;</span>
              </>
            ) : null}
            {' '}is already registered for this admin account but it was never verified. Complete setup
            with the code from your authenticator app, or start over by removing the pending factor.
          </p>

          {error && (
            <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </p>
          )}

          <button
            className="mt-6 w-full rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={submitting}
            onClick={handleContinuePending}
            type="button"
          >
            Continue with existing factor
          </button>

          <button
            className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={submitting}
            onClick={() => void handleUnenrollPending()}
            type="button"
          >
            {submitting ? 'Removing pending factor...' : 'Start over'}
          </button>
        </div>
      </div>
    );
  }

  const heading =
    gateState === 'setup'
      ? 'Set up multi-factor authentication'
      : 'Verify your admin access';

  const description =
    gateState === 'setup'
      ? 'Admin accounts must enroll a TOTP authenticator before entering the admin panel.'
      : 'Enter the current code from your authenticator app to continue to the admin panel.';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-gray-900">{heading}</h1>
        <p className="mt-3 text-sm text-gray-600">{description}</p>

        {error && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}

        {gateState === 'setup' && !enrollment && (
          <button
            className="mt-6 w-full rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={submitting}
            onClick={() => void handleEnroll()}
            type="button"
          >
            {submitting ? 'Starting MFA setup...' : 'Start MFA setup'}
          </button>
        )}

        {gateState === 'setup' && enrollment && (
          <>
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <canvas
                ref={qrCanvasRef}
                aria-label="Admin MFA QR code"
                className="mx-auto h-56 w-56 rounded-lg bg-white p-2"
                role="img"
                width={224}
                height={224}
              />
              <p className="mt-4 text-sm text-gray-600">
                If you can&apos;t scan the QR code, use this secret in your authenticator app:
              </p>
              <code className="mt-2 block rounded-lg bg-white px-3 py-2 text-sm text-gray-900">
                {enrollment.secret}
              </code>
            </div>

            <label className="mt-6 block text-sm font-semibold text-gray-800">
              Verification code
            </label>
            <input
              className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setChallengeCode(event.target.value)}
              placeholder="Enter 6-digit code"
              type="text"
              value={challengeCode}
            />
            <button
              className="mt-4 w-full rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={submitting}
              onClick={() => void handleVerifyCode()}
              type="button"
            >
              {submitting ? 'Verifying MFA...' : 'Enable MFA'}
            </button>
          </>
        )}

        {gateState === 'challenge' && (
          <>
            <label className="mt-6 block text-sm font-semibold text-gray-800">Authenticator code</label>
            <input
              className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setChallengeCode(event.target.value)}
              placeholder="Enter 6-digit code"
              type="text"
              value={challengeCode}
            />
            <button
              className="mt-4 w-full rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={submitting}
              onClick={() => void handleVerifyCode()}
              type="button"
            >
              {submitting ? 'Verifying MFA...' : 'Verify admin session'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMfaGate;
